import React, { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { EmblaOptionsType } from "embla-carousel";

import {
    BULLION,
    BULLION_KEY,
    BULLION_PRODUCT,
    BullionInvestmentType,
    LatestDetails,
    PRODUCT_CURRENCY,
    PRODUCT_WEIGHT,
    ProductDataResponse,
} from "@/app/alts/bullion/type-constants";
import useMobile from "@/app/hooks/use-mobile";
import { formatIndianCurrency } from "@/app/utils/helper";
import { getBullionPrices } from "@/app/api/service/bullion";
import { Input } from "@/components/ui/input";
import LoadingIcon from "@/app/assets/icons/loading";
import BullionCarousel from "./bullion-carousel";

interface Props {
    type?: BULLION_KEY;
    setProductsData: (res: ProductDataResponse) => void;
}

const deBounceTimeout: number = 1000;
const OPTIONS: EmblaOptionsType = { axis: "y", loop: true };
const SLIDES = [
    "Hedge against inflation",
    "Positive return in 9 out of 10yrs",
    "Value increase by 204% in 8yrs",
];

const OneTimeCalc: FC<Props> = memo(function OneTimeCalc({ type, setProductsData }) {
    const { isMobile } = useMobile();
    const TYPE = type ?? BULLION_PRODUCT.GOLD.TYPE;
    const [priceValue, setPriceValue] = useState<string>("");
    const [priceDisplayValue, setPriceDisplayValue] = useState<string>(
        formatIndianCurrency(Number(priceValue)),
    );
    const [activeField, setActiveField] = useState<string>("");
    const [quantity, setQuantity] = useState<string>("1");
    const [displayQuantity, setDisplayQuantity] = useState<string>(quantity + PRODUCT_WEIGHT.GRAM);
    const [latestDetails, setLatestDetails] = useState<LatestDetails | null>(null);
    const [isPriceFetching, setIsPriceFetching] = useState(false);
    const [hasError, setHasError] = useState(false);

    const refPrice = useRef<HTMLInputElement>(null);
    const refGm = useRef<HTMLInputElement>(null);

    function debounce<T extends (...args: (string | number | boolean)[]) => void>(
        func: T,
        delay: number,
        noExecute?: boolean,
    ): (...args: Parameters<T>) => void {
        let timeoutId: ReturnType<typeof setTimeout>;
        return (...args: Parameters<T>) => {
            clearTimeout(timeoutId);
            if (!noExecute) {
                timeoutId = setTimeout(() => {
                    func(...args);
                }, delay);
            }
        };
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const parts = val.replace(/[^\d.]/g, "").split(".");
        let value = "";
        if (parts.length > 2) {
            value = parts.shift() + "." + parts.join("");
        } else {
            value = parts.join(".");
        }
        const parsedValue = parseFloat(value);
    
        if (activeField === "price") {
            if (
                typeof parsedValue != "number" ||
                parsedValue == Number.NEGATIVE_INFINITY ||
                parsedValue == Number.POSITIVE_INFINITY ||
                parsedValue < BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MIN_AMT
            ) {
                setPriceValue(value);
                setQuantity("0");
                setHasError(true);
                debouncedLog(value, activeField, true);
            } else if (parsedValue > BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MAX_AMT) {
                setPriceValue(value);
                setQuantity("0");
                setHasError(true);
                debouncedLog(value, activeField, true);
            } else {
                setPriceValue(value);
                setHasError(false);
                debouncedLog(value, activeField);
            }
        } else if (activeField === "gm") {
            if (
                typeof parsedValue != "number" ||
                parsedValue == Number.NEGATIVE_INFINITY ||
                parsedValue == Number.POSITIVE_INFINITY ||
                parsedValue <= 0
            ) {
                setPriceValue("");
                setQuantity(value);
                setHasError(true);
                debouncedLog(value, activeField, true);
            } else {
                setQuantity(value);
                // Set error to false initially, but it will be set to true in the API response
                // if the resulting price is below minimum amount
                setHasError(false);
                debouncedLog(value, activeField);
            }
        }
    };
    

    const handleChangeOnBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const parsedValue = parseFloat(val);

        if (String(parsedValue) == "NaN" || (typeof parsedValue == "number" && parsedValue <= 0)) {
            setPriceValue("0");
            setPriceDisplayValue(formatIndianCurrency(Number(0)));
            setQuantity("0");
            setDisplayQuantity("0" + PRODUCT_WEIGHT.GRAM);
            setHasError(true);
        }

        setActiveField("");
    };

    const debouncedLog = useCallback(
        debounce((value, unit, noExecute = false) => {
            if (
                typeof value === "string" &&
                (parseFloat(value) >=
                    BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MIN_AMT ||
                    parseFloat(value) <=
                        BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MAX_AMT)
            ) {
                fnGetBullionPrices(String(value), String(unit), Boolean(noExecute));
            }
        }, deBounceTimeout),
        [],
    );

    const fnGetBullionPrices = async (value?: string, unit?: string, noExecute?: boolean) => {
        if (!noExecute) {
            setIsPriceFetching(true);
            await getBullionPrices({
                product_code: BULLION[TYPE].CODE,
                quantity: !unit || unit == "undefined" ? quantity : value,
                time_frame: "1Y",
                unit_type:
                    !unit || unit == "undefined"
                        ? BULLION_PRODUCT[TYPE].WEIGHT_SYMBOL
                        : unit === "price"
                          ? BULLION_PRODUCT[TYPE].PRICE_SYMBOL
                          : BULLION_PRODUCT[TYPE].WEIGHT_SYMBOL,
            })
                .then((response) => {
                    if (response.success && response.data.latest_details) {
                        const amount = response.data.latest_details.amount;
                        
                        // Check if the resulting amount is below minimum or above maximum when input is from gm field
                        if (unit === "gm") {
                            if (amount < BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MIN_AMT || 
                                amount > BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MAX_AMT) {
                                setHasError(true);
                                setPriceValue(String(amount)); // Set the actual calculated amount
                            } else {
                                setHasError(false);
                                setProductsData(response);
                            }
                        } else {
                            setHasError(false);
                            setProductsData(response);
                        }
                        
                        setLatestDetails(response.data.latest_details);
                        if (typeof window != "undefined") {
                            window.focus();
                        }
                    } else {
                        console.error("No latest details found in response");
                        setLatestDetails(null);
                    }
                    setIsPriceFetching(false);
                })
                .catch((error) => {
                    setIsPriceFetching(false);
                    console.error("Error fetching bullion prices: ", error);
                    throw error;
                });
        }
    };
    useEffect(() => {
            fnGetBullionPrices("1", "weight");

    }, [TYPE]);

    useEffect(() => {
        if (
            BULLION[TYPE].LAST_FETCHED.AMOUNT <
            BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MIN_AMT
        ) {
            fnGetBullionPrices("1", "weight");
        } else {
            setPriceValue(String(BULLION[TYPE].LAST_FETCHED.AMOUNT));
            setPriceDisplayValue(formatIndianCurrency(Number(BULLION[TYPE].LAST_FETCHED.AMOUNT)));
            setQuantity(String(BULLION[TYPE].LAST_FETCHED.WEIGHT));
            setDisplayQuantity(String(BULLION[TYPE].LAST_FETCHED.WEIGHT) + PRODUCT_WEIGHT.GRAM);
        }
    }, [TYPE]);

    useEffect(() => {
        if (latestDetails?.amount && latestDetails?.grams) {
            const price = latestDetails?.amount || 0;
            const weight = latestDetails?.grams || 1; // default value "1"
            BULLION[TYPE].LAST_FETCHED = { AMOUNT: price, WEIGHT: weight };

            setPriceValue(String(price));
            setQuantity(String(weight));
            setIsPriceFetching(false);
        }
    }, [latestDetails?.amount, latestDetails?.grams]);

    useEffect(() => {
        setPriceDisplayValue(formatIndianCurrency(Number(priceValue)));
        setDisplayQuantity(quantity + PRODUCT_WEIGHT.GRAM);
    }, [priceValue, quantity]);

    return (
        <>
            <div className="flex flex-col px-s7 py-s9 md:px-s9 md:pb-8 md:pt-4">
                <h5
                    id="title-onetime-investment"
                    className="mb-1 text-center text-lg font-semibold leading-[130%]"
                >
                    Make a 1-time investment
                </h5>
                <p
                    id="sub-title-amt-quantity"
                    className="text-center text-sm font-regular leading-[160%] text-neutral-500 md:text-base"
                >
                    Choose an amount or quantity
                </p>
                <div className="py-s9">
                    <div
                        id="block-calc"
                        className={`relative flex min-h-[50px] items-center rounded-[12px] border md:mb-4 md:rounded-xl ${
                            isPriceFetching && (!priceValue || priceValue?.trim() == "0")
                                ? "border-error-600"
                                : "border-neutral-100"
                        }`}
                    >
                        <div
                            className={
                                "border-re relative min-h-[46px] rounded-xl border border-transparent pl-s7 " +
                                (activeField === "price"
                                    ? " " +
                                      "[&>span]:-top-[8px] [&>span]:bg-white [&>span]:text-sm [&>span]:opacity-100"
                                    : "")
                            }
                        >
                            <span className="absolute -top-[5px] left-[12px] bg-transparent px-1 text-sm leading-none opacity-0 transition-all duration-150 ease-in-out">{`${PRODUCT_CURRENCY[BULLION[BULLION.TYPE].CURRENCY]}`}</span>

                            <Input
                                ref={refPrice}
                                id="price"
                                name="price"
                                type={activeField === "price" && isMobile ? "number" : "text"}
                                value={activeField === "price" ? priceValue : priceDisplayValue}
                                onChange={handleChange}
                                onFocus={(e) => {
                                    setActiveField(e.target.name);
                                }}
                                onBlur={handleChangeOnBlur}
                                className={
                                    "h-[44px] !border-0 border-transparent p-0 text-left !text-base !font-regular !outline-none focus-visible:!ring-0 focus-visible:!ring-transparent focus-visible:!ring-offset-0 " +
                                    (isPriceFetching ? "touch-none text-neutral-300" : "")
                                }
                                // disabled={isPriceFetching}
                                autoComplete="off"
                            />
                        </div>
                        <div
                            className={
                                "absolute left-[50%] top-[8px] h-[calc(100%-16px)] w-[1px] " +
                                (isPriceFetching ? "" : "bg-neutral-100")
                            }
                        >
                            {isPriceFetching && (
                                <div className="absolute left-[calc((100%-24px)/2)] top-[calc((100%-24px)/2)] h-6 w-6">
                                    <LoadingIcon className="m-auto h-6 w-6 animate-spin text-neutral-500" />
                                </div>
                            )}
                        </div>
                        <div
                            className={
                                "relative min-h-[46px] rounded-xl border border-transparent pr-s7 " +
                                (activeField === "gm"
                                    ? " " +
                                      "[&>span]:-top-[10px] [&>span]:bg-white [&>span]:text-sm [&>span]:opacity-100"
                                    : "")
                            }
                        >
                            <span className="absolute -top-[5px] right-[12px] bg-transparent px-1 text-sm leading-none opacity-0 transition-all duration-150 ease-in-out">{`${PRODUCT_WEIGHT[BULLION[BULLION.TYPE].UNIT]}`}</span>
                            <Input
                                ref={refGm}
                                id="gm"
                                name="gm"
                                type={activeField === "gm" && isMobile ? "number" : "text"}
                                value={activeField === "gm" ? quantity : displayQuantity}
                                onChange={handleChange}
                                onFocus={(e) => {
                                    setActiveField(e.target.name);
                                }}
                                onBlur={handleChangeOnBlur}
                                className={
                                    "h-[44px] !border-0 border-transparent p-0 text-right !text-base !font-regular !outline-none focus-visible:!ring-0 focus-visible:!ring-transparent focus-visible:!ring-offset-0 " +
                                    (isPriceFetching ? "touch-none text-neutral-300" : "")
                                }
                                spellCheck="false"
                                // disabled={isPriceFetching}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    {hasError ? (
                        <div
                            className={`mb-2 flex translate-y-0 transform items-center gap-2 opacity-100 transition-all duration-500`}
                        >
                            <Info width="12" height="12" className="text-error-600" />
                            <p className="text-sm text-error-600">
                                {Number(priceValue) >
                                BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MAX_AMT
                                    ? `Maximum amount should be ${BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MAX_AMT}`
                                    : `Minimum amount should be ${BULLION_PRODUCT[TYPE][BullionInvestmentType.ONETIME].MIN_AMT}`}
                            </p>
                        </div>
                    ) : null}

                    <BullionCarousel slides={SLIDES} options={OPTIONS} />

                    {/* <p
                        id="info-container"
                        className="mt-s7 flex items-center gap-2 text-left text-sm leading-[160%] text-neutral-500 md:mt-0 md:text-sm"
                    >
                        <button id="info-btn" className="group relative">
                            <Info
                                id="info-icon"
                                size={15}
                                className={`cursor-default text-black`}
                            />
                            <div
                                id="info-text"
                                className="absolute left-0 top-full mt-2 hidden w-56 rounded-md bg-black p-1 text-xs text-white group-hover:block sm:left-0 sm:mx-0 md:left-0 md:w-80"
                            >
                                The market average returns for {TYPE?.toLowerCase()} are 11%
                                annually.
                            </div>
                        </button>
                    </p> */}
                </div>
                {/* <div id="block-total-gained" className="flex items-center justify-between">
                    <p id="name" className="text-sm font-semibold leading-[160%] md:text-base">
                        Total Gained
                    </p>
                    <p id="value" className="text-sm font-semibold leading-[160%] md:text-base">
                        {formatIndianCurrency(21500)}
                    </p>
                </div> */}
            </div>
        </>
    );
});

export default OneTimeCalc;
